<template>
  <div>
    <div class="flex justify-center">
      <div class="flex h-full w-full flex-col justify-center p-24 md:w-[34rem]">
        <RevStepper
          v-if="canRenderStepper"
          :active-step="STEP_NAMES.ADDRESS"
          :alternative-text-back="i18n(translations.previousPage)"
          :alternative-text-close="i18n(translations.stepperClose)"
          :alternative-text-completed="i18n(translations.stepperCompleted)"
          :alternative-text-current="i18n(translations.stepperCurrent)"
          :has-close="false"
          :hasBack="hasBackButton"
          :steps
          @back="goBack"
        />
        <div v-else class="flex flex-col items-center">
          <RevSkeleton
            class="my-10"
            height="24px"
            shape="rectangle"
            width="50%"
          />
          <RevSkeleton class="mt-8" height="2px" shape="line" width="100%" />
        </div>
        <div class="flex flex-col justify-start pt-32">
          <h2 class="body-1-bold">
            {{ i18n(translations.formTitle) }}
          </h2>

          <p class="body-1 my-16">
            {{ i18n(translations.formDescription) }}
          </p>

          <FormGenerator
            v-if="formConfig"
            activate-address-validation-error-logger
            :form-config
            :form-id="ADDRESS_FORM"
            :has-submit-button="false"
            @form-submit="handleSubmit"
          />
          <template v-else>
            <RevSkeleton
              v-for="index in 5"
              :key="`form-skeleton-${index}`"
              class="mt-24 !h-48"
              shape="rectangle"
            />
          </template>

          <small
            v-if="buybackConfig.hasGdprLegalMentions"
            class="caption mt-24"
          >
            <FormattedMessage :definition="translations.legalText">
              <template #link>
                <RevLink target="_blank" :to="legalLink">
                  {{ i18n(translations.legalLinkText) }}
                </RevLink>
              </template>
            </FormattedMessage>
          </small>

          <small v-if="buybackConfig.hasSmsMentions" class="caption mt-24">
            <FormattedMessage :definition="translations.smsText">
              <template #link>
                <RevLink target="_blank" :to="termsLink">
                  {{ i18n(translations.termsLinkText) }}
                </RevLink>
              </template>
            </FormattedMessage>
          </small>

          <RevButton
            id="address-submit"
            class="ml-auto mt-24"
            data-qa="adress-submit"
            :disabled="isLoadingAddress"
            :form="ADDRESS_FORM"
            full-width="adaptive"
            :loading="isLoadingAddress"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.formSubmitButton) }}
          </RevButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, inject, onMounted, ref, toRef, watch } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import type {
  FormValues,
  Input,
  InputBase,
} from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import FormGenerator from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.vue'
import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/composables/config/useBuybackConfig'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { decamelizeKeys } from '@backmarket/utils/object/decamelizeKeys'
import { RevButton } from '@ds/components/Button'
import { getPhoneNumberInfos } from '@ds/components/InputPhone'
import { RevLink } from '@ds/components/Link'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevStepper } from '@ds/components/Stepper'
import type { CountryCode } from 'libphonenumber-js'

import { useBuybackFunnelOffer } from '~/scopes/buyback/composables/useBuybackFunnelOffer'
import { useResaleStepper } from '~/scopes/buyback/composables/useResaleStepper'
import { useGenerateAddressForm } from '~/scopes/buyback/config/useGenerateAddressForm'
import { FUNNEL_ERROR_MESSAGE } from '~/scopes/buyback/pages/constants'
import { CMS } from '~/scopes/cms/routes-names'

import type { ErrorData } from '../components/TheCatcher/useCatcher'
import { ERROR_TYPES, ROUTE_NAMES, STEP_NAMES } from '../constants'

import translations from './Address.translations'

const route = useRoute()
const currentLocale = useI18nLocale()
const buybackConfig = useBuybackConfig()
const { offer } = useBuybackFunnelOffer()
const $httpFetch = useHttpFetch()

const { openErrorToast } = useTheToast()
const i18n = useI18n()
const { user } = useUserStore()
const tracking = useTracking()
const logger = useLogger()
const { getDynamicAddressFieldValidators } = useDynamicAddressFieldValidators()
const hasShippingStep = computed(() => offer.value?.shipping.hasChoice ?? false)
const { steps, goBack, hasBackButton } = useResaleStepper({
  activeStepName: STEP_NAMES.ADDRESS,
  hasShippingStep: toRef(hasShippingStep),
})

const isLoadingAddress = ref(true)
const addressPublicId = ref('')
const formConfig = ref<Input[] | null>(null)
const canRenderStepper = computed(() => {
  return Boolean(offer.value) && steps.value.length > 0
})

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'Address',
}

const ADDRESS_FORM = 'address-form'
const legalLink = `${currentLocale}/legal/data-protection`
const termsLink = `${currentLocale}/legal/terms-of-trade-in`

const { data: initialAddressFormConfig, error: initialAddressFormConfigError } =
  useAsyncData(
    async () => {
      isLoadingAddress.value = true

      return $httpFetch('/bm/buyback/v1/customer/address')
    },
    {
      server: false,
    },
  )

watch(
  [initialAddressFormConfig, initialAddressFormConfigError],
  async () => {
    // If customer address is not existing the API resolves in 404
    // in this case it will fail silently and user will have to fill in the address
    if (
      initialAddressFormConfigError.value &&
      initialAddressFormConfigError.value.statusCode !== 404
    ) {
      logger.error(FUNNEL_ERROR_MESSAGE.GET_ADDRESS, {
        error: initialAddressFormConfigError.value as HttpApiError | Error,
        owners: ['bot-squad-circularity-offer-front'],
      })

      isLoadingAddress.value = false

      return
    }

    const addressForm = useGenerateAddressForm(initialAddressFormConfig.value)

    const initialFormValidation = addressForm.reduce(
      (acc, input) => ({ ...acc, [input.id]: input.validation || [] }),
      {} as Record<string, InputBase['validation']>,
    )

    const addressValidation = await getDynamicAddressFieldValidators({
      featureFlag: 'FF_ENABLE_ADDRESS_FIELD_VALIDATION_TRADEIN',
      initialFormValues: initialFormValidation,
      fallbackValidators: initialFormValidation,
      metadata: { scope: 'customer' },
    })

    formConfig.value = addressForm.map((input) => {
      return {
        ...input,
        validation:
          (addressValidation &&
            (addressValidation[
              input.id as keyof typeof addressValidation
            ] as InputBase['validation'])) ||
          input.validation,
      }
    })

    isLoadingAddress.value = false
  },
  { deep: true },
)

async function handleSubmit(values: FormValues) {
  isLoadingAddress.value = true
  tracking.trackClick({
    zone: 'buyback',
    name: 'personal_info',
    value: {
      category: route.params?.id,
    },
  })

  const { dial, nationalNumber } = getPhoneNumberInfos(
    values.phone as string,
    values.country as CountryCode,
  )
  const body = {
    ...values,
    firstName: user.value.firstName,
    lastName: user.value.lastName,
    countryDialInCode: dial,
    phone: nationalNumber,
  }

  try {
    const payload = await $httpFetch('/bm/buyback/v1/customer/address', {
      body: decamelizeKeys(
        body,
      ) as ApiSpecs['/bm/buyback/v1/customer/address']['POST']['body'],
      method: 'POST',
    })

    addressPublicId.value = payload?.public_id
  } catch (errors) {
    logger.error(FUNNEL_ERROR_MESSAGE.POST_ADDRESS, {
      owners: ['bot-squad-circularity-offer-front'],
    })

    let formattedErrors = ''
    Object.keys(body).forEach((key) => {
      const err = (errors as Record<string, Array<string>>)[key]
      if (err) {
        formattedErrors += `${err.join('\n')}\n`
      }
    })

    openErrorToast({ content: formattedErrors })
    isLoadingAddress.value = false

    return
  }

  if (offer.value?.shipping.hasChoice && addressPublicId.value) {
    await navigateTo({
      name: ROUTE_NAMES.SHIPPING,
      params: {
        ...route.params,
        addressId: addressPublicId.value,
      },
      query: route.query,
    })
  } else if (addressPublicId.value) {
    try {
      const payload = await $httpFetch('/bm/sourcing/order/new', {
        body: {
          listing_id: route.params?.listingId?.toString(),
          customerAddressId: addressPublicId.value,
          shippingId: offer.value?.shipping.default,
        },
        method: 'POST',
      })

      tracking.trackBuybackConfirmation({
        buybackType: 'bb_normal',
        buybackOrderId: payload.id,
        commission: payload.estimatedCommission?.amount,
        conversionValue: payload.price,
        conversionCurrency: payload.estimatedCommission?.currency,
      })

      await navigateTo({
        name: ROUTE_NAMES.CONFIRMATION,
        params: {
          id: payload.id,
        },
        query: route.query,
      })
    } catch (postOrderError) {
      isLoadingAddress.value = false
      if (postOrderError instanceof HttpApiError) {
        if (postOrderError.type === ERROR_TYPES.INVALID_ID_ERROR_TYPE) {
          openErrorToast({
            content: i18n(translations.formErrorUnableToProcess),
          })
        } else if (postOrderError.type === ERROR_TYPES.NOT_FOUND_ERROR_TYPE) {
          openErrorToast({
            content: i18n(translations.formErrorListingNotFound),
          })
        } else {
          openErrorToast({
            content: i18n(translations.formApiError),
          })
        }
      } else {
        openErrorToast({
          content: i18n(translations.formApiError),
        })
      }
    }
  }
}

onMounted(async () => {
  if (!offer.value) {
    await navigateTo({
      name: CMS.BUYBACK,
      params: {
        pageName: 'home',
      },
    })
  }
})
</script>
